import { Button, CircularProgress, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import type { PublicCollectionData } from '@mogul/api-client';
import BigNumber from 'bignumber.js';
import type { ReactNode } from 'react';
import React, { useState } from 'react';

import { ordersApi } from '../../../../api';
import { buyUrl } from '../../../../config';
import detailsStyles from '../../../../containers/pages/nft-details/NftDetailsPage.module.css';
import { useWeb3State } from '../../../../hooks';
import type { Nft } from '../../../../hooks/nft-api.hook';
import { useSeaport } from '../../../../hooks/seaport-hook';
import type { Asset } from '../../../../lib/graph/MogulMarketplace';
import { Button as WhimsicalButton } from '../../../core/button/Button';
import { Panel } from '../../panel/Panel.component';
import styles from './MakeNftOrderModal.module.css';

enum ItemType {
  NATIVE = 0,
  ERC20 = 1,
  ERC721 = 2,
  ERC1155 = 3,
  ERC721_WITH_CRITERIA = 4,
  ERC1155_WITH_CRITERIA = 5,
}

const MOGUL_DECIMALS = 18;

const useStyles = makeStyles({
  mogulInput: {
    padding: 0,
    '& .MuiFilledInput-underline::before': {
      borderBottom: '0px',
    },
    '& .MuiFilledInput-underline::after': {
      borderBottom: '2px solid #FFDA5D',
    },
  },
});

const MakeNftOrderTextRow = ({
  label,
  text,
}: {
  label?: string | number | ReactNode;
  text?: string | number | ReactNode;
}) => (
  <div className={`flex justify-between py-2`}>
    <p>{label}</p>
    <p className={`text-right`}>{text}</p>
  </div>
);

interface MakeNftOrderModalControlProps {
  hideModal: () => void;
}

const MakeNftOrderModalControl: React.FC<MakeNftOrderModalControlProps> = ({
  hideModal,
}) => (
  <div className={`flex justify-between`}>
    <h2 className={`m-0`}>Make an offer</h2>
    <div
      role="button"
      tabIndex={0}
      onClick={hideModal}
      onKeyDown={hideModal}
      className={`cursor-pointer`}
    >
      <CloseIcon />
    </div>
  </div>
);

interface MakeNftOrderModalHeaderProps {
  dbNft?: Nft | null;
  collection?: PublicCollectionData | null;
  goToCollectionsPage?: () => void;
  image?: string;
  hideModal: () => void;
}

const MakeNftOrderModalHeader: React.FC<MakeNftOrderModalHeaderProps> = ({
  dbNft,
  collection,
  goToCollectionsPage,
  image,
  hideModal,
}) => (
  <div className="flex flex-row justify-left align-center gap-4">
    {image != null && (
      <img className="h-28 w-28 rounded-xl" src={image} alt="nft" />
    )}
    <div className="h-fit">
      <p className={styles.modalTitle}>{dbNft?.title}</p>
      {collection != null && (
        <p className={detailsStyles.collection}>
          Collection:{' '}
          <button
            onClick={() => {
              hideModal();
              goToCollectionsPage?.();
            }}
            className={detailsStyles.collectionLink}
          >
            {collection.name}
          </button>
        </p>
      )}
    </div>
  </div>
);

interface MakeNftOrderPricesProps {
  balance?: string;
  floorPrice?: string;
  bestOffer?: string;
}

const MakeNftOrderPrices: React.FC<MakeNftOrderPricesProps> = ({
  balance,
  floorPrice,
  bestOffer,
}) => {
  const numberBalance = Math.trunc(Number(balance) * 100) / 100;

  return (
    <div className={styles.darkContainer}>
      {[
        ['Balance', numberBalance],
        ['Floor price', floorPrice],
        [
          'Best offer',
          bestOffer != null
            ? BigNumber(bestOffer)
                .div(10 ** MOGUL_DECIMALS)
                .toFixed(1)
            : 0,
        ],
      ].map(([label, value]) => (
        <MakeNftOrderTextRow
          key={label}
          label={label}
          text={`${value ?? 0} MOGUL`}
        />
      ))}
    </div>
  );
};

interface MakeNftOrderMogulAmountInputProps {
  mogulAmount?: string;
  setMogulAmount: (value?: string) => void;
  maxAmount?: string;
  quantity?: number;
  validationError?: string | null;
  setValidationError: (value: string | null) => void;
}

const MakeNftOrderMogulAmountInput: React.FC<MakeNftOrderMogulAmountInputProps> =
  ({
    mogulAmount,
    setMogulAmount,
    maxAmount,
    quantity,
    validationError: error,
    setValidationError: setError,
  }) => {
    const classes = useStyles();

    const handleInput = (value: string) => {
      let _value = value;
      // temporary decimals validation
      const [wholePart, decimalPart] = _value.split('.');
      if (decimalPart && decimalPart.length > MOGUL_DECIMALS) {
        _value = `${wholePart}.${decimalPart.substring(0, MOGUL_DECIMALS)}`;
      }

      setMogulAmount(_value);

      if (Number.isNaN(Number(_value))) {
        setError('Invalid amount');
        return;
      }

      const bigNumberValueWithDecimals = new BigNumber(_value).times(
        new BigNumber(10).pow(MOGUL_DECIMALS),
      );

      if (
        bigNumberValueWithDecimals.isLessThan(new BigNumber(0)) ||
        bigNumberValueWithDecimals.isGreaterThan(
          new BigNumber(maxAmount || '0'),
        )
      ) {
        setError('Amount is out of range');
        return;
      }

      setError(null);
    };

    return (
      <div>
        <TextField
          fullWidth
          variant="filled"
          value={mogulAmount}
          onChange={e => handleInput(e.target.value)}
          error={!!error}
          InputProps={{
            endAdornment: <>MOGUL</>,
            style: { color: 'white' },
          }}
          inputProps={{
            style: { padding: '1rem' },
          }}
          className={`${styles.mogulInput} ${classes.mogulInput}`}
        />
        <MakeNftOrderTextRow
          label={<p style={{ color: 'darkred' }}>{error}</p>}
          text={`Total offer amount: ${new BigNumber(
            (!Number.isNaN(Number(mogulAmount)) ? Number(mogulAmount) : 0) *
              (quantity ?? 0),
          ).toString(10)} MOGUL`}
        />
      </div>
    );
  };

interface MakeNftOrderQuantityInputProps {
  available?: number;
  quantity?: number;
  setQuantity: (value: (q: number) => number) => void;
}

const MakeNftOrderQuantityInput: React.FC<MakeNftOrderQuantityInputProps> = ({
  available,
  quantity,
  setQuantity,
}) => {
  const classes = useStyles();

  return (
    <TextField
      variant="filled"
      value={quantity}
      InputProps={{
        endAdornment: (
          <Button
            style={{ color: 'white', padding: 0, fontSize: 20, minWidth: 32 }}
            onClick={() => setQuantity(q => (q < (available ?? 1) ? q + 1 : q))}
          >
            +
          </Button>
        ),
        startAdornment: (
          <Button
            style={{ color: 'white', padding: 0, fontSize: 20, minWidth: 32 }}
            onClick={() => setQuantity(q => (q > 1 ? q - 1 : q))}
          >
            –
          </Button>
        ),
        style: { color: 'white' },
      }}
      inputProps={{
        style: { padding: '1rem', width: 24, textAlign: 'center' },
      }}
      className={`${styles.mogulInput} ${classes.mogulInput}`}
    />
  );
};

interface MakeNftOrderQuantityBlockProps
  extends MakeNftOrderQuantityInputProps {}

const MakeNftOrderQuantityBlock: React.FC<MakeNftOrderQuantityBlockProps> = ({
  available,
  ...props
}) => (
  <div className="flex flex-row justify-between items-center gap-1">
    <div>
      <p className={styles.modalTitle}>Quantity</p>
      <p>{available} available</p>
    </div>
    <MakeNftOrderQuantityInput available={available ?? 1} {...props} />
  </div>
);

interface MakeNftOrderExpiredInputProps {
  expiredAt?: string;
  setExpiredAt: (value?: string) => void;
}

const MakeNftOrderExpiredInput: React.FC<MakeNftOrderExpiredInputProps> = ({
  expiredAt,
  setExpiredAt,
}) => {
  const classes = useStyles();

  return (
    <TextField
      variant="filled"
      value={expiredAt}
      onChange={e => setExpiredAt(e.target.value)}
      type="datetime-local"
      inputProps={{
        style: { padding: '1rem', color: 'white' },
      }}
      className={`${styles.mogulInput} ${classes.mogulInput}`}
    />
  );
};

interface MakeNftOrderExpiredBlockProps extends MakeNftOrderExpiredInputProps {}

const MakeNftOrderExpiredBlock: React.FC<MakeNftOrderExpiredBlockProps> =
  props => (
    <div className="flex flex-row justify-between items-center gap-1">
      <p className={styles.modalTitle}>Expired at</p>
      <MakeNftOrderExpiredInput {...props} />
    </div>
  );

interface MakeNftOrderFooterProps {
  onMakeOffer: () => void;
  disabled: boolean;
  loading: boolean;
}

const MakeNftOrderFooter: React.FC<MakeNftOrderFooterProps> = ({
  onMakeOffer,
  disabled,
  loading,
}) => (
  <div className="flex flex-row gap-8">
    {loading ? (
      <div className={'flex w-full justify-center items-center'}>
        <CircularProgress />
      </div>
    ) : (
      <WhimsicalButton
        className="w-full"
        onClick={onMakeOffer}
        disabled={disabled}
      >
        Make offer
      </WhimsicalButton>
    )}
    <WhimsicalButton
      className={`w-full ${styles.buyMogulButton}`}
      onClick={() => window.open(buyUrl, '_blank')}
    >
      Buy Mogul
    </WhimsicalButton>
  </div>
);

export interface MakeNftOrderModalProps {
  asset: Asset;
  ownedQuantity: number;
  floorPrice?: string;
  bestOffer?: string;
  image?: string;
  dbNft?: Nft | null;
  collection?: PublicCollectionData | null;
  goToCollectionsPage?: () => void;
}

export interface MakeNftOrderProps extends MakeNftOrderModalProps {
  hideModal: () => void;
}

const MakeNftOrder: React.FC<MakeNftOrderProps> = ({
  asset,
  hideModal,
  floorPrice,
  bestOffer,
  image,
  dbNft,
  collection,
  goToCollectionsPage,
}) => {
  const { seaport, createOrder } = useSeaport();
  const { walletDetails } = useWeb3State();

  const [mogulAmount, setMogulAmount] = useState<string>();
  const [quantity, setQuantity] = useState<number>(1);
  const [expiredAt, setExpiredAt] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [amountInputValidationError, setAmountInputValidationError] =
    useState<string | null>(null);

  const makeOfferHandler = async () => {
    if (!seaport || !createOrder || !mogulAmount) {
      return;
    }

    setLoading(true);

    const multipliedMogulAmountWithDecimals = new BigNumber(mogulAmount)
      .times(quantity)
      .times(new BigNumber(10).pow(MOGUL_DECIMALS))
      .toString(10);

    const commonParams = {
      offerAmount: multipliedMogulAmountWithDecimals,
      nftContractAddress: asset.tokenAddress,
      nftId: `${asset.tokenId}`,
      nftAmount: quantity,
      nftType: ItemType[asset.type ?? 'ERC721'],
    };

    const seaportResult = await createOrder({
      ...commonParams,
      offerer: walletDetails.address ?? '',
      expiredAt: new Date(`${expiredAt}`), // UTC?
    });

    const orderHash = seaport.getOrderHash(seaportResult.parameters);
    console.log('SEAPORT ORDER HASH', orderHash);

    try {
      await ordersApi.createOrder({
        orderCreateData: {
          signature: seaportResult.signature,
          ...seaportResult.parameters,
          offer: undefined,
          consideration: undefined,
          ...commonParams,
        },
        network: walletDetails.chain.type,
      });
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
    hideModal();
  };

  const disabled =
    amountInputValidationError !== null ||
    !Number(mogulAmount) ||
    !expiredAt ||
    new Date(expiredAt) < new Date();

  return (
    <Panel
      className="overflow-hidden"
      bodyClassName={styles.modalPanel + ' flex flex-col rounded-lg'}
    >
      <MakeNftOrderModalControl hideModal={hideModal} />
      <MakeNftOrderModalHeader
        dbNft={dbNft}
        collection={collection}
        goToCollectionsPage={goToCollectionsPage}
        image={image}
        hideModal={hideModal}
      />
      <MakeNftOrderPrices
        balance={walletDetails.balance.stars.formatted}
        floorPrice={floorPrice}
        bestOffer={bestOffer}
      />
      <MakeNftOrderMogulAmountInput
        mogulAmount={mogulAmount}
        setMogulAmount={setMogulAmount}
        maxAmount={walletDetails.balance.stars.raw.toString()}
        quantity={quantity}
        validationError={amountInputValidationError}
        setValidationError={setAmountInputValidationError}
      />
      <MakeNftOrderQuantityBlock
        available={asset.totalSupply ?? 1}
        quantity={quantity}
        setQuantity={setQuantity}
      />
      <MakeNftOrderExpiredBlock
        expiredAt={expiredAt}
        setExpiredAt={setExpiredAt}
      />
      <MakeNftOrderFooter
        onMakeOffer={makeOfferHandler}
        disabled={disabled}
        loading={loading}
      />
    </Panel>
  );
};

export const MakeNftOrderModal =
  (props: MakeNftOrderModalProps) =>
  ({ hideModal }: MakeNftOrderProps) => {
    return <MakeNftOrder hideModal={hideModal} {...props} />;
  };
